/**
 * Canonical serialization + content hashing for sim states (AD-007a). The hash
 * is what the regression suite and any future lockstep-multiplayer desync check
 * compare, so the *string* has to be canonical: object keys are emitted in
 * sorted order and numbers use JavaScript's shortest round-trip form.
 *
 * The digest is FNV-1a, run twice with different offset bases and combined into
 * 64 bits. It is a fingerprint, not a cryptographic hash — it exists to make
 * "did this tick produce a different state" cheap to answer.
 */

const FNV_PRIME_32 = 0x01000193;
const FNV_OFFSET_A = 0x811c9dc5;
const FNV_OFFSET_B = 0x9e3779b9;

/** FNV-1a over the UTF-16 code units of `text`, returned as an unsigned 32-bit integer. */
export function fnv1a32(text: string, offsetBasis: number = FNV_OFFSET_A): number {
  let hash = offsetBasis >>> 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    hash = Math.imul(hash ^ (code & 0xff), FNV_PRIME_32) >>> 0;
    hash = Math.imul(hash ^ (code >>> 8), FNV_PRIME_32) >>> 0;
  }
  return hash >>> 0;
}

function hex8(value: number): string {
  return (value >>> 0).toString(16).padStart(8, "0");
}

/** 64-bit content fingerprint of an arbitrary string, as 16 lowercase hex digits. */
export function contentHash(text: string): string {
  return hex8(fnv1a32(text, FNV_OFFSET_A)) + hex8(fnv1a32(text, FNV_OFFSET_B));
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * JSON with deterministic key order. Non-finite numbers throw rather than
 * silently becoming `null`: a NaN in the sim state is a physics bug, and
 * hashing over it would hide the divergence it causes.
 */
export function canonicalJson(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new RangeError(`cannot canonicalize non-finite number: ${value}`);
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(",")}]`;
  }
  if (isPlainRecord(value)) {
    const fields = Object.keys(value)
      .sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`);
    return `{${fields.join(",")}}`;
  }
  throw new TypeError(`cannot canonicalize value of type ${typeof value}`);
}
